import React, { useState, useMemo } from 'react';
import type { HomeAddressData } from '../../../types/customer-portal';
import SearchableDropdown from '../../../../../components/common/SearchableDropdown';
import {
  isValidPhilippineZipCode
} from '../../../../../data/mockLocationData';
import { useLocalAddressesProvinces } from '../../../hooks/useLocalAddressesProvinces';
import { useLocalAddressesCities } from '../../../hooks/useLocalAddressesCities';
import { useLocalAddressesBarangays } from '../../../hooks/useLocalAddressesBarangays';
import { getProvinceCodeFromDescription } from '../../../utils/provinceMapper';
import { getZipCodeFromBarangay, shouldAutoPopulateZipCode } from '../../../utils/barangayZipCodeUtils';
import './HomeAddressStep.css';
import '../../../../../styles/searchable-dropdown.css';
import '../../../styles/zipCodeAutoPopulation.css';

// Import validation utilities
import {
  validateAddressField,
  filterAddressField,
  ADDRESS_FIELD_MAX_LENGTH
} from '../../../../../shared/utils/fieldValidation';

// Field validation highlighting interface
interface FieldValidationErrors {
  [key: string]: boolean;
}

interface HomeAddressStepProps {
  data?: HomeAddressData;
  onNext: (data: HomeAddressData) => void;
  onBack: () => void;
}

const HomeAddressStep: React.FC<HomeAddressStepProps> = ({
  data,
  onNext,
  onBack
}) => {
  const [formData, setFormData] = useState<HomeAddressData>({
    houseUnitNo: data?.houseUnitNo || '',
    streetName: data?.streetName || '',
    subdivisionVillage: data?.subdivisionVillage || '',
    barangay: data?.barangay || '',
    city: data?.city || '',
    province: data?.province || '',
    zipCode: data?.zipCode || '',
    country: 'Philippines' // Always set to Philippines for Home Address
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isZipCodeAutoPopulated, setIsZipCodeAutoPopulated] = useState(false);
  
  // Field validation highlighting state
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  // Use the local addresses provinces hook
  const { provinces, provinceOptions, loading: provincesLoading, error: provincesError } = useLocalAddressesProvinces();
  
  // Get selected province code for city filtering
  const selectedProvinceCode = useMemo(() => {
    return getProvinceCodeFromDescription(provinces, formData.province);
  }, [provinces, formData.province]);

  // Use the local addresses cities hook with province filtering
  const { cityOptions, loading: citiesLoading, error: citiesError} = useLocalAddressesCities(selectedProvinceCode);

  // Use the local addresses barangays hook with city filtering
  const { barangays, barangayOptions, loading: barangaysLoading, error: barangaysError} = useLocalAddressesBarangays(formData.city);

  // Form validation logic
  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    if (!formData.houseUnitNo.trim() && !formData.streetName.trim()) {
      newErrors.address = 'House/Unit No. or Street Name is required';
    }

    // Enhanced validation with character limits and patterns
    if (formData.houseUnitNo && !validateAddressField(formData.houseUnitNo)) {
      newErrors.houseUnitNo = 'House/Unit No. contains invalid characters';
    }
    if (formData.streetName && !validateAddressField(formData.streetName)) {
      newErrors.streetName = 'Street name contains invalid characters';
    }
    if (formData.subdivisionVillage && !validateAddressField(formData.subdivisionVillage)) {
      newErrors.subdivisionVillage = 'Subdivision/Village contains invalid characters';
    }

    if (!formData.barangay.trim()) {
      newErrors.barangay = 'Barangay is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.province.trim()) {
      newErrors.province = 'Province is required';
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.country === 'Philippines' && !isValidPhilippineZipCode(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid 4-digit Philippine postal code';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    return newErrors;
  };

  // Get validation errors for required fields (for highlighting)
  const getValidationErrors = (): FieldValidationErrors => {
    const errors: FieldValidationErrors = {};
    
    // Address field (House/Unit No., Street Name, Subdivision/Village) - required
    if (!formData.houseUnitNo.trim() && !formData.streetName.trim()) {
      errors.address = true;
    }
    
    // All other fields are required
    if (!formData.barangay.trim()) errors.barangay = true;
    if (!formData.city.trim()) errors.city = true;
    if (!formData.province.trim()) errors.province = true;
    if (!formData.zipCode.trim()) errors.zipCode = true;
    if (!formData.country.trim()) errors.country = true;
    
    return errors;
  };

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? 'field-error' : '';
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };


  const handleInputChange = (field: keyof HomeAddressData, value: string) => {
    // Prevent country field changes - always keep it as Philippines
    if (field === 'country') {
      return;
    }
    
    // Clear field error when user starts typing
    clearFieldError(field);
    
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Clear city when province changes
      if (field === 'province' && value !== prev.province) {
        newData.city = '';
        newData.barangay = ''; // Also clear barangay when province changes
        newData.zipCode = ''; // Clear zip code when province changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Clear barangay when city changes
      if (field === 'city' && value !== prev.city) {
        newData.barangay = '';
        newData.zipCode = ''; // Clear zip code when city changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Auto-populate zip code when barangay is selected
      if (field === 'barangay') {
        if (value && shouldAutoPopulateZipCode(value, barangays)) {
          const zipCode = getZipCodeFromBarangay(barangays, value);
          if (zipCode) {
            newData.zipCode = zipCode;
            setIsZipCodeAutoPopulated(true);
          } else {
            // If no zip code found, allow manual entry
            setIsZipCodeAutoPopulated(false);
          }
        } else {
          // If barangay is cleared, reset zip code and auto-population flag
          newData.zipCode = '';
          setIsZipCodeAutoPopulated(false);
        }
      }
      
      return newData;
    });

    // Clear error for this field when user starts typing (existing error messages)
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        
        // Also clear city and barangay errors when province changes
        if (field === 'province') {
          delete newErrors.city;
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Also clear barangay error when city changes
        if (field === 'city') {
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Clear zip code error when barangay changes
        if (field === 'barangay') {
          delete newErrors.zipCode;
        }
        
        return newErrors;
      });
    }

    // Clear address error when either house/unit no or street name is filled
    if ((field === 'houseUnitNo' || field === 'streetName') && errors.address) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
      // Also clear field error for address highlighting
      clearFieldError('address');
    }

    // Clear related field errors when dependencies change
    if (field === 'province') {
      clearFieldError('city');
      clearFieldError('barangay');
      clearFieldError('zipCode');
    }
    
    if (field === 'city') {
      clearFieldError('barangay');
      clearFieldError('zipCode');
    }
    
    if (field === 'barangay') {
      clearFieldError('zipCode');
    }
  };

  const handleNext = () => {
    console.log('HomeAddressStep: Next button clicked');
    
    // Check for validation errors
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;
    
    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      
      // Also set error messages for display
      const errorMessages = validateForm();
      setErrors(errorMessages);
      
      console.log('HomeAddressStep: Validation errors:', validationErrors);
      return; // Don't proceed if there are validation errors
    }
    
    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);
    setErrors({});
    
    console.log('HomeAddressStep: Calling onNext with data:', formData);
    onNext(formData);
  };

  const handleBack = () => {
    console.log('HomeAddressStep: Back button clicked');
    onBack();
  };

  // Combine house/unit no and street name for the address field
  const getAddressValue = () => {
    const parts = [formData.houseUnitNo, formData.streetName].filter(Boolean);
    return parts.join(' ');
  };

  const handleAddressChange = (value: string) => {
    // Apply character filtering for address field
    const filteredValue = filterAddressField(value);
    
    // For simplicity, we'll put the entire address in streetName
    // In a real implementation, you might want to parse this more intelligently
    setFormData(prev => ({
      ...prev,
      houseUnitNo: '',
      streetName: filteredValue
    }));

    // Clear address error
    if (errors.address) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }
    
    // Clear field error for address highlighting
    clearFieldError('address');
  };

  return (
    <>
      <div className="home-address-step">
        <div className="step-header">
          <h3 className="step-title">Home Address</h3>
          <p className="step-description">Please provide the current residential address of the Principal Insured.</p>
        </div>

        <div className="step-form">
          {/* First Row: Address and Province */}
          <div className="form-row address-row-1">
            <div className={`form-group address-group ${getFieldErrorClass('address')}`}>
              <label className="form-label">House/Unit No., Street Name, Subdivision/Village *</label>
              <input
                type="text"
                className={`form-input ${errors.address ? 'error' : ''}`}
                value={getAddressValue()}
                onChange={(e) => handleAddressChange(e.target.value)}
                placeholder="Enter your complete address"
                maxLength={ADDRESS_FIELD_MAX_LENGTH}
              />
            </div>

            <div className={`form-group ${getFieldErrorClass('province')}`}>
              <label className="form-label">Province *</label>
              <SearchableDropdown
                options={provinceOptions}
                selectedValue={formData.province}
                onChange={(value) => handleInputChange('province', value)}
                placeholder={provincesLoading ? "Loading provinces..." : "Select Province"}
                searchPlaceholder="Search provinces..."
                className={errors.province ? 'error' : ''}
                error={!!errors.province}
                position="below"
                dropdownId="province"
                disabled={provincesLoading || !!provincesError}
              />
            </div>
          </div>

          {/* Second Row: City, Barangay, Zip Code, Country */}
          <div className="form-row address-row-2">
            <div className={`form-group ${getFieldErrorClass('city')}`}>
              <label className="form-label">City *</label>
              <SearchableDropdown
                options={cityOptions}
                selectedValue={formData.city}
                onChange={(value) => handleInputChange('city', value)}
                placeholder={
                  !formData.province
                    ? "Select City"
                    : citiesLoading
                      ? "Loading cities..."
                      : "Select City"
                }
                searchPlaceholder="Search cities..."
                className={errors.city ? 'error' : ''}
                error={!!errors.city}
                position="below"
                dropdownId="city"
                disabled={!formData.province || citiesLoading || !!citiesError}
              />
            </div>

            <div className={`form-group ${getFieldErrorClass('barangay')}`}>
              <label className="form-label">Barangay *</label>
              <SearchableDropdown
                options={barangayOptions}
                selectedValue={formData.barangay}
                onChange={(value) => handleInputChange('barangay', value)}
                placeholder={
                  !formData.city
                    ? "Select Barangay"
                    : barangaysLoading
                      ? "Loading barangays..."
                      : "Select Barangay"
                }
                searchPlaceholder="Search barangays..."
                className={errors.barangay ? 'error' : ''}
                error={!!errors.barangay}
                position="below"
                dropdownId="barangay"
                disabled={!formData.city || barangaysLoading || !!barangaysError}
              />
            </div>

            <div className={`form-group ${getFieldErrorClass('zipCode')}`}>
              <label className="form-label">Zip Code *</label>
              <input
                type="text"
                className={`form-input ${errors.zipCode ? 'error' : ''} ${isZipCodeAutoPopulated ? 'auto-populated' : ''}`}
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                placeholder={isZipCodeAutoPopulated ? "Auto-populated from barangay" : "1234"}
                maxLength={4}
                readOnly={isZipCodeAutoPopulated}
              />
            </div>

            <div className={`form-group ${getFieldErrorClass('country')}`}>
              <label className="form-label">Country *</label>
              <SearchableDropdown
                options={['Philippines']}
                selectedValue="Philippines"
                onChange={() => {}} // No-op function since it's disabled
                placeholder="Philippines"
                searchPlaceholder="Philippines"
                className={`${errors.country ? 'error' : ''} disabled-country-field`}
                error={!!errors.country}
                position="below"
                dropdownId="country"
                disabled={true} // Always disabled for Home Address
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          type="button"
          className="btn-back"
          onClick={handleBack}
        >
          Back
        </button>
        <button
          type="button"
          className="btn-next"
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </>
  );
};

export default HomeAddressStep;